import { toDate } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { isToday } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';

import { ApiScanNote } from '@kaiizen/kai-pro-api';
import { useScanPartNotes, useUser } from '@kaiizen/kai-pro-api-react-state';
import {
  FullStatusDetails,
  ProgressUpdate,
  ScanPartContext,
  ScanPartOptimized,
  ScanPhoto,
  WORK_STATUS_ORDER,
  WorkStatus,
} from '@kaiizen/kai-scan-template-system';

import { useBulkPhotoUploader } from '@/lib/photoLib';
import { formatDate } from '@kaiizen/kai-presenters';
import nextIcon from '../../../Assets/next.svg';
import prevIcon from '../../../Assets/prev.svg';
import { UploadIndicatorIcon } from '../../../Icons';
import { useNavigateToPhoto, usePhotoUriForLayoutSize, useShowNotes } from '../../../State';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../../Styles';
import { Button, NotesCountBubble, PhotoDropZone, RadioIconWidget } from '../../../Widgets';
import { AddPhotoIcon } from '../../../Widgets/StyledIcons';
import { NoPhotoSvg } from './NoPhotoSvg';
import { WorkStatusIcon } from './WorkStatusIcons';

// ********************************************************************************
// Styles
// ********************************************************************************

const SCAN_PART_PROGRESS_CONTAINER_STYLE = css`
  label: SCAN_PART_PROGRESS_COLUMN_LIST_STYLE;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 400px;
  min-height: 0;
`;

const SCAN_PART_PHOTO_WINDOW_STYLE = css`
  label: SCAN_PART_PHOTO_WINDOW_STYLE;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 4 / 3;
  flex: 0 0 auto;
  overflow: hidden;
  border-bottom: 1px solid ${COLORS2.alwaysDark.darkGray};
  cursor: pointer;

  &:hover button[data-scan-part-photo-nav='true'] {
    opacity: 1;
    pointer-events: auto;
  }
`;

const SCAN_PART_PHOTO_WINDOW_NAV_BUTTON_STYLE = css`
  label: SCAN_PART_PHOTO_WINDOW_NAV_BUTTON_STYLE;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 53px;
  height: 71px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const SCAN_PART_PHOTO_WINDOW_UPLOAD_INDICATOR_STYLE = css`
  label: SCAN_PART_PHOTO_WINDOW_UPLOAD_INDICATOR_STYLE;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 12;
  pointer-events: none;
`;

const SCAN_PART_PHOTO_WINDOW_PREV_BUTTON_STYLE = css`
  label: SCAN_PART_PHOTO_WINDOW_PREV_BUTTON_STYLE;
  left: 0px;
`;

const SCAN_PART_PHOTO_WINDOW_NEXT_BUTTON_STYLE = css`
  label: SCAN_PART_PHOTO_WINDOW_NEXT_BUTTON_STYLE;
  right: 0px;
`;

const SCAN_PART_PHOTO_WINDOW_IMAGE_STYLE = css`
  label: SCAN_PART_PHOTO_WINDOW_IMAGE_STYLE;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ADD_PHOTO_CORNER_BUTTON_STYLE = css`
  label: ADD_PHOTO_CORNER_BUTTON_STYLE;
  position: absolute;
  right: ${LAYOUT2.spacing1}px;
  bottom: ${LAYOUT2.spacing1}px;
  width: 44px;
  height: 44px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.65);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.6);
    outline-offset: 2px;
  }
`;

const ADD_PHOTO_CORNER_BUTTON_ICON_STYLE = css`
  label: ADD_PHOTO_CORNER_BUTTON_ICON_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HIDDEN_FILE_INPUT_STYLE = css`
  label: HIDDEN_FILE_INPUT_STYLE;
  display: none;
`;

const SCAN_PART_PROGRESS_COLUMN_STATUS_CONTAINER_STYLE = css`
  label: SCAN_PART_PROGRESS_COLUMN_STATUS_CONTAINER_STYLE;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
  background-color: var(--background1);
  border: 1px solid ${COLORS2.alwaysDark.darkGray};
`;

const SIMPLE_WORK_STATUS_BUTTON_STYLE = css`
  label: SIMPLE_WORK_STATUS_BUTTON_STYLE;
  display: flex;
  align-items: center;
  padding-left: ${LAYOUT2.spacing1}px;
  width: 100%;
  height: 45px;
  gap: ${LAYOUT2.spacing1}px;
  cursor: pointer;
  &:hover {
    background-color: ${COLORS2.alwaysDark.lightGray};
  }
`;

const SIMPLE_WORK_STATUS_BUTTON_DISABLED_STYLE = css`
  label: SIMPLE_WORK_STATUS_BUTTON_DISABLED_STYLE;
  opacity: 0.67;
  cursor: not-allowed;
  &:hover {
    background-color: var(--background1);
  }
`;

const WORK_STATUS_DISPLAY_NAME_STYLE = css`
  label: WORK_STATUS_DISPLAY_NAME_STYLE;
  ${TYPOGRAPHY2.small};
  color: var(--text);
  flex-grow: 1;
`;

const WORK_STATUS_DISPLAY_NAME_SELECTED_STYLE = css`
  label: WORK_STATUS_DISPLAY_NAME_SELECTED_STYLE;
  ${TYPOGRAPHY2.smallBold};
  color: var(--text);
`;

const WORK_STATUS_ICON_STYLE = css`
  label: WORK_STATUS_ICON_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
`;

const WORK_STATUS_DISPLAY_NAME_MAP: { [key in WorkStatus]: string } = {
  notStarted: 'Not Started',
  inProgress: 'In Progress',
  needsRework: 'Rework',
  completed: 'Completed',
};

const DUE_DATE_ROW_STYLE = css`
  label: DUE_DATE_ROW_STYLE;
  display: flex;
  align-items: center;
  padding: ${LAYOUT2.spacing1}px;
  gap: ${LAYOUT2.spacing2}px;
  border-top: 1px solid ${COLORS2.alwaysDark.darkGray};
`;

const DUE_DATE_LABEL_STYLE = css`
  label: DUE_DATE_LABEL_STYLE;
  ${TYPOGRAPHY2.xSmall};
  color: var(--text2);
  white-space: nowrap;
`;

const DUE_DATE_INPUT_STYLE = css`
  label: DUE_DATE_INPUT_STYLE;
  ${TYPOGRAPHY2.small};
  color: var(--text);
  background: var(--background);
  border: 1px solid var(--border);
  padding: ${LAYOUT2.spacingHalf}px ${LAYOUT2.spacing1}px;
  flex: 1;
  min-width: 0;
`;

const LABELED_CHECKBOX_CONTAINER_STYLE = css`
  label: LABELED_CHECKBOX_CONTAINER_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacingHalf}px;
  cursor: pointer;
`;

const LABELED_CHECKBOX_LABEL_STYLE = css`
  label: LABELED_CHECKBOX_LABEL_STYLE;
  ${TYPOGRAPHY2.xSmallBold};
  color: var(--text);
  user-select: none;
`;

const LABELED_CHECKBOX_INPUT_STYLE = css`
  label: LABELED_CHECKBOX_INPUT_STYLE;
  width: 18px;
  height: 18px;
  accent-color: ${COLORS2.alwaysDark.green};
`;

const SCAN_PART_NO_PHOTO_NO_ADD_PHOTO_STYLE = css`
  label: SCAN_PART_NO_PHOTO_NO_ADD_PHOTO_STYLE;
  width: 100%;
  height: 100%;
  background: var(--background3);
`;

const UPDATED_AT_AND_BY_INFO_STYLE = css`
  label: UPDATED_AT_AND_BY_INFO_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${LAYOUT2.spacingHalf}px;
  gap: ${LAYOUT2.spacing1}px;
`;

const SMALL_LABEL_STYLE = css`
  label: SMALL_LABEL_STYLE;
  ${TYPOGRAPHY2.small};
  color: var(--text2);
`;

const SMALL_VALUE_STYLE = css`
  label: UPDATED_BY_LABEL_STYLE;
  ${TYPOGRAPHY2.smallBold};
  color: var(--text2);
`;

const LABELED_VALUE_CONTAINER_STYLE = css`
  label: LABELED_VALUE_CONTAINER_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacingHalf}px;
`;

const SCAN_PART_PROGRESS_SCROLL_AREA_STYLE = css`
  label: SCAN_PART_PROGRESS_SCROLL_AREA_STYLE;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;

  > * {
    flex-shrink: 0;
  }
`;

const ADD_INSTRUCTIONS_BUTTON_CONTAINER_STYLE = css`
  label: ADD_INSTRUCTIONS_BUTTON_CONTAINER_STYLE;
  display: flex;
  justify-content: center;
  padding: ${LAYOUT2.spacing1}px;
`;

const INSTRUCTIONS_CONTAINER_STYLE = css`
  label: INSTRUCTIONS_CONTAINER_STYLE;
  display: flex;
  flex-direction: column;
  padding: ${LAYOUT2.spacing1}px;
  gap: ${LAYOUT2.spacing1}px;
`;

const SINGLE_INSTRUCTIONS_CONTAINER_STYLE = css`
  label: INSTRUCTIONS_CONTAINER_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacingHalf}px;
`;

const INSTRUCTIONS_LABEL_STYLE = css`
  label: INSTRUCTIONS_LABEL_STYLE;
  ${TYPOGRAPHY2.smallBold};
  color: var(--text2);
`;

const INSTRUCTIONS_VALUE_STYLE = css`
  label: INSTRUCTIONS_VALUE_STYLE;
  ${TYPOGRAPHY2.small};
  color: var(--text);
`;

const INSTRUCTIONS_INFO_STYLE = css`
  label: INSTRUCTIONS_INFO_STYLE;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  gap: ${LAYOUT2.spacingQuarter}px;
  ${TYPOGRAPHY2.xSmall};
  color: var(--text2);
`;

// ********************************************************************************
// Helper Components
// ********************************************************************************

const SimpleWorkStatusButton = ({ status, selected, onStatusChange }: {
  status: WorkStatus;
  selected: boolean;
  onStatusChange?: (update: Partial<ProgressUpdate>) => void;
}) => (
  <div css={[SIMPLE_WORK_STATUS_BUTTON_STYLE, !onStatusChange && SIMPLE_WORK_STATUS_BUTTON_DISABLED_STYLE]} onClick={() => onStatusChange?.({ status })}>
    <WorkStatusIcon status={status} black />
    <div css={[WORK_STATUS_DISPLAY_NAME_STYLE, selected && WORK_STATUS_DISPLAY_NAME_SELECTED_STYLE]}>{WORK_STATUS_DISPLAY_NAME_MAP[status]}</div>
    <div css={WORK_STATUS_ICON_STYLE}>
      <RadioIconWidget checked={selected} onChange={() => onStatusChange?.({ status })} />
    </div>
  </div>
);

const LabeledCheckbox = ({ checked, onChange, label, disabled }: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
}) => (
  <div css={LABELED_CHECKBOX_CONTAINER_STYLE} onClick={() => !disabled && onChange(!checked)}>
    <input
      css={LABELED_CHECKBOX_INPUT_STYLE}
      type='checkbox'
      checked={checked}
      onChange={event => event.target.checked !== checked && onChange(event.target.checked)}
      disabled={disabled}
    />
    {label && <span css={LABELED_CHECKBOX_LABEL_STYLE}>{label}</span>}
  </div>
);

const formatDateForInput = (date: Date | undefined): string => date ? toDate(date).toISOString().split('T')[0] : '';

const parseDateFromInput = (value: string): Date | undefined => value ? toDate(value) : undefined;

const DueDateRow = ({ fullStatusDetails, onStatusChange }: {
  fullStatusDetails: FullStatusDetails;
  onStatusChange?: (update: Partial<ProgressUpdate>) => void;
}) => {
  const disabled = !onStatusChange;
  const dueDate = formatDateForInput(fullStatusDetails.dueDate);
  const asap = fullStatusDetails.neededAsap ?? false;

  return (
    <div css={DUE_DATE_ROW_STYLE}>
      <span css={DUE_DATE_LABEL_STYLE}>Due</span>
      <input
        css={DUE_DATE_INPUT_STYLE}
        type='date'
        value={dueDate}
        onChange={event => onStatusChange?.({ dueDate: parseDateFromInput(event.target.value) })}
        disabled={disabled}
      />
      <LabeledCheckbox
        checked={asap}
        onChange={neededAsap => onStatusChange?.({ neededAsap })}
        label='ASAP'
        disabled={disabled}
      />
    </div>
  );
};

const ScanPartPhotoWindow = ({ scanId, groupId, taskId, photosDate, photos, scanPart, scanPartContext, editable }: {
  scanId: string;
  groupId?: string;
  taskId?: string;
  photosDate?: Date;
  photos: ScanPhoto[];
  scanPart: ScanPartOptimized;
  scanPartContext: ScanPartContext;
  editable?: boolean;
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(photos.length - 1);
  useEffect(() => {
    setCurrentPhotoIndex(photos.length - 1);
  }, [photos]);
  const currentPhoto = photos[currentPhotoIndex];
  // The card is 400px wide and its photo window 4:3.
  const uri = usePhotoUriForLayoutSize(currentPhoto?.photoId, 400);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasPhoto = !!uri;
  const navigateToPhoto = useNavigateToPhoto();
  const hasMultiplePhotos = photos.length > 1;
  const bulkPhotoUploader = useBulkPhotoUploader();

  useEffect(() => {
    setCurrentPhotoIndex(previousPhotoIndex => Math.min(previousPhotoIndex, Math.max(photos.length - 1, 0)));
  }, [photos.length]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    bulkPhotoUploader(event.target.files, scanPart, scanPartContext);
    event.target.value = '';
  };

  const openFilePicker = () => editable && fileInputRef.current?.click();
  const goToPreviousPhoto = () => setCurrentPhotoIndex(previousPhotoIndex => (previousPhotoIndex - 1 + photos.length) % photos.length);
  const goToNextPhoto = () => setCurrentPhotoIndex(previousPhotoIndex => (previousPhotoIndex + 1) % photos.length);

  return (
    <PhotoDropZone scanPart={editable ? scanPart : null} scanPartContext={scanPartContext}>
      <div
        css={SCAN_PART_PHOTO_WINDOW_STYLE}
        onClick={() =>
          currentPhoto?.photoId
            ? navigateToPhoto({
              groupId,
              taskId,
              photoId: currentPhoto.photoId,
              date: photosDate ? toDate(photosDate).toISOString().split('T')[0] : undefined,
            })
            : openFilePicker()}
      >
        {currentPhoto?.photoSource === 'upload' && (
          <div css={SCAN_PART_PHOTO_WINDOW_UPLOAD_INDICATOR_STYLE}>
            <UploadIndicatorIcon size={32} />
          </div>
        )}
        {hasMultiplePhotos && (
          <>
            <button
              type='button'
              data-scan-part-photo-nav='true'
              css={[SCAN_PART_PHOTO_WINDOW_NAV_BUTTON_STYLE, SCAN_PART_PHOTO_WINDOW_PREV_BUTTON_STYLE]}
              aria-label='Previous photo'
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                goToPreviousPhoto();
              }}
            >
              <img src={prevIcon} alt='Previous' width={53} height={71} />
            </button>
            <button
              type='button'
              data-scan-part-photo-nav='true'
              css={[SCAN_PART_PHOTO_WINDOW_NAV_BUTTON_STYLE, SCAN_PART_PHOTO_WINDOW_NEXT_BUTTON_STYLE]}
              aria-label='Next photo'
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                goToNextPhoto();
              }}
            >
              <img src={nextIcon} alt='Next' width={53} height={71} />
            </button>
          </>
        )}
        {hasPhoto
          ? <img css={SCAN_PART_PHOTO_WINDOW_IMAGE_STYLE} src={uri} />
          : editable
          ? <NoPhotoSvg />
          : <div css={SCAN_PART_NO_PHOTO_NO_ADD_PHOTO_STYLE} />}
        {photos.length > 1 && <NotesCountBubble count={photos.length} backgroundColor='var(--background)' size='large' />}
        {editable && hasPhoto && (
          <button
            type='button'
            css={ADD_PHOTO_CORNER_BUTTON_STYLE}
            aria-label='Add photo'
            onClick={event => {
              event.stopPropagation();
              openFilePicker();
            }}
          >
            <span css={ADD_PHOTO_CORNER_BUTTON_ICON_STYLE}>
              <AddPhotoIcon color='white' width={26} height={22} />
            </span>
          </button>
        )}
        {editable && (
          <input
            ref={fileInputRef}
            type='file'
            accept='image/*'
            multiple
            css={HIDDEN_FILE_INPUT_STYLE}
            onClick={event => event.stopPropagation()}
            onChange={handleFileSelect}
          />
        )}
      </div>
    </PhotoDropZone>
  );
};

const UpdatedAtAndByInfo = ({ updatedAt, updatedByUserId, editable }: {
  updatedAt?: Date;
  updatedByUserId?: string;
  editable?: boolean;
}) => {
  const { user } = useUser(updatedByUserId);
  const displayName = user?.name || user?.phoneNumber || 'Unknown User';
  return (
    <div css={UPDATED_AT_AND_BY_INFO_STYLE}>
      {updatedAt && (
        <div css={LABELED_VALUE_CONTAINER_STYLE}>
          <span css={SMALL_LABEL_STYLE}>updated:</span>
          <span css={SMALL_VALUE_STYLE}>{isToday(updatedAt) ? 'today' : formatDate(updatedAt)}</span>
        </div>
      )}
      {updatedAt && updatedByUserId && (
        <div css={LABELED_VALUE_CONTAINER_STYLE}>
          <span css={SMALL_LABEL_STYLE}>by:</span>
          <span css={SMALL_VALUE_STYLE}>{displayName}</span>
        </div>
      )}
      {!updatedAt && !updatedByUserId && (!editable ? <div css={SMALL_VALUE_STYLE}>(no previous status)</div> : <div css={SMALL_VALUE_STYLE}>Today</div>)}
    </div>
  );
};

const InlineInstructions = ({ note }: { note: ApiScanNote }) => {
  const { user } = useUser(note.userId);
  const displayName = user?.name || user?.phoneNumber || 'Unknown User';
  return (
    <div css={SINGLE_INSTRUCTIONS_CONTAINER_STYLE}>
      <div css={INSTRUCTIONS_VALUE_STYLE}>{note.body}</div>
      <div css={INSTRUCTIONS_INFO_STYLE}>
        {formatDate(note.createdAt)} by {displayName}
      </div>
    </div>
  );
};

// ********************************************************************************
// Main Component
// ********************************************************************************

export type ProgressCardProps = {
  /** The scan part this card represents (needed for notes editor) */
  scanPart: ScanPartOptimized;
  /** The scan ID for photo/note lookups */
  scanId: string;
  /** Group ID for navigation */
  groupId?: string;
  /** Task ID for navigation */
  taskId?: string;
  /** The full status details for this card (photos, notes, status, etc.) */
  fullStatusDetails: FullStatusDetails;
  /** Whether this card is editable (the current/today card) */
  editable?: boolean;
  /** Callback when status fields are changed */
  onStatusChange?: (update: Partial<ProgressUpdate>) => void;
};

export const TaskProgressCard = ({
  scanPart,
  scanId,
  groupId,
  taskId,
  fullStatusDetails,
  editable,
  onStatusChange,
}: ProgressCardProps) => {
  const { showNotesEditor } = useShowNotes();
  const { scanPartNotes: scanNotes } = useScanPartNotes(scanPart.id);
  const filteredScanNotes = scanNotes.filter(note => (!!editable === isToday(note.createdAt)) && note.scanNoteType === 'instructions');

  return (
    <div css={SCAN_PART_PROGRESS_CONTAINER_STYLE}>
      <div css={SCAN_PART_PROGRESS_COLUMN_STATUS_CONTAINER_STYLE}>
        <div css={SCAN_PART_PROGRESS_SCROLL_AREA_STYLE}>
          <ScanPartPhotoWindow
            scanId={scanId}
            groupId={groupId}
            taskId={taskId}
            photosDate={fullStatusDetails.updatedAt}
            photos={fullStatusDetails.photos}
            scanPart={scanPart}
            scanPartContext={{ groupId, taskId }}
            editable={editable}
          />
          <UpdatedAtAndByInfo
            updatedAt={fullStatusDetails.updatedAt}
            updatedByUserId={fullStatusDetails.updatedByUserId}
            editable={editable}
          />
          {WORK_STATUS_ORDER.map(status => {
            const selected = fullStatusDetails.status === status;
            if (!editable && !selected) return null;
            return (
              <SimpleWorkStatusButton
                key={status}
                status={status}
                selected={selected}
                onStatusChange={onStatusChange}
              />
            );
          })}
          {filteredScanNotes.length > 0 && (
            <div css={INSTRUCTIONS_CONTAINER_STYLE}>
              <div css={INSTRUCTIONS_LABEL_STYLE}>Instructions:</div>
              {filteredScanNotes.map(note => <InlineInstructions note={note} key={note.id} />)}
            </div>
          )}
          {editable && (
            <div css={ADD_INSTRUCTIONS_BUTTON_CONTAINER_STYLE}>
              <Button onClick={() => showNotesEditor({ scanPart, allowedNoteTypes: ['instructions'] })}>
                Add Instructions
              </Button>
            </div>
          )}
        </div>
        <DueDateRow fullStatusDetails={fullStatusDetails} onStatusChange={onStatusChange} />
      </div>
    </div>
  );
};
